import React, {
    useEffect,
    useState,
    useRef,
    forwardRef,
    useImperativeHandle,
    useCallback,
} from "react";
import { useField } from "formik";
import Typography from "../Typography/Typography";
import "./form.scss";

const SUPPORTED_TYPES = {
    image: {
        extensions: ["jpg", "jpeg", "png"],
        mimeTypes: ["image/jpeg", "image/png", "image/jpg"],
    },
    document: {
        extensions: ["pdf", "doc", "docx", "xls", "xlsx"],
        mimeTypes: [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ],
    },
    mixed: {
        extensions: ["png", "jpg", "jpeg", "pdf"],
        mimeTypes: ["image/png", "image/jpeg", "image/jpg", "application/pdf"],
    },
};

const UnifiedFileUpload = forwardRef((props, ref) => {
    const {
        name,
        width,
        height,
        onFileSelected,
        selectedFile,
        initialFile,
        label,
        fileType = "all",
        customFileTypes,
        maxFileSize = 5 * 1024 * 1024,
        maxFiles = 5,
        currentUrl,
        value,
        required,
        multiple = false,
        ...rest
    } = props;

    const [field, meta, helpers] = useField(name);
    const [file, setFile] = useState(
        multiple &&
            (fileType === "image" ||
                fileType === "document" ||
                fileType === "mixed")
            ? initialFile || []
            : null,
    );
    const [preview, setPreview] = useState(
        multiple &&
            (fileType === "image" ||
                fileType === "document" ||
                fileType === "mixed")
            ? (initialFile || []).map((f) => f.url)
            : initialFile?.url || null,
    );
    const [localError, setLocalError] = useState(null);
    const inputRef = useRef(null);
    const hasManuallyRemoved = useRef(false);

    // Added to prevent infinite loop
    const initializedRef = useRef(false);

    const getAllowedTypes = useCallback(() => {
        // If customFileTypes is provided, use that instead
        if (customFileTypes) {
            return customFileTypes;
        }

        if (fileType === "image") return SUPPORTED_TYPES.image;
        if (fileType === "document") return SUPPORTED_TYPES.document;
        if (fileType === "mixed") return SUPPORTED_TYPES.mixed;
        return {
            extensions: [
                ...SUPPORTED_TYPES.image.extensions,
                ...SUPPORTED_TYPES.document.extensions,
            ],
            mimeTypes: [
                ...SUPPORTED_TYPES.image.mimeTypes,
                ...SUPPORTED_TYPES.document.mimeTypes,
            ],
        };
    }, [fileType, customFileTypes]);

    // Helper to validate a single file
    const validateFile = useCallback(
        (file) => {
            if (!file) return null;

            const allowedTypes = getAllowedTypes();
            const fileExtension = file.name.split(".").pop().toLowerCase();

            if (
                !allowedTypes.extensions.includes(fileExtension) &&
                !allowedTypes.mimeTypes.includes(file.type)
            ) {
                return `Unsupported file type. Please upload: ${allowedTypes.extensions.join(
                    ", ",
                )}`;
            }

            if (file.size > maxFileSize) {
                return `File size exceeds ${
                    maxFileSize / (1024 * 1024)
                }MB limit.`;
            }

            return null;
        },
        [getAllowedTypes, maxFileSize],
    );

    const handleMultipleFiles = useCallback(
        (selectedFiles) => {
            let validFiles = [];
            let previews = [];
            let error = null;

            // Combine existing files with new ones
            const existingFiles = Array.isArray(file) ? file : [];
            const existingPreviews = Array.isArray(preview) ? preview : [];

            // Optionally, filter out duplicates by name
            const existingFileNames = new Set(existingFiles.map((f) => f.name));

            // Fix: Check if we're actually adding new files that would exceed the limit
            const newFilesToAdd = selectedFiles.filter(
                (f) => !existingFileNames.has(f.name),
            );

            // Enforce maximum files limit - only check if we're adding new files
            if (existingFiles.length + newFilesToAdd.length > maxFiles) {
                const errorMessage = `You can upload a maximum of ${maxFiles} files.`;
                setLocalError(errorMessage);
                helpers.setError(errorMessage);
                return;
            }

            for (let f of selectedFiles) {
                if (existingFileNames.has(f.name)) continue; // skip duplicates
                const err = validateFile(f);
                if (err) {
                    error = err;
                    helpers.setError(err);
                    break;
                }
                validFiles.push(f);
                // For images, create preview URLs. For documents and mixed types with images, create preview if it's an image
                if (
                    (fileType === "image" || fileType === "mixed") &&
                    f.type?.startsWith("image/")
                ) {
                    previews.push(URL.createObjectURL(f));
                } else {
                    previews.push(null); // No preview for documents
                }
            }
            if (error) {
                setLocalError(error);
                return;
            }
            hasManuallyRemoved.current = false;
            setLocalError(null);
            helpers.setError(null);
            const allFiles = [...existingFiles, ...validFiles];
            const allPreviews = [...existingPreviews, ...previews];
            setFile(allFiles);
            setPreview(allPreviews);
            helpers.setValue(allFiles);
            onFileSelected?.(allFiles);
        },
        [
            validateFile,
            helpers,
            onFileSelected,
            file,
            preview,
            fileType,
            maxFiles,
        ],
    );

    const handleFile = useCallback(
        (selectedFile) => {
            if (
                multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
            ) {
                if (!selectedFile || selectedFile.length === 0) {
                    setFile([]);
                    setPreview([]);
                    helpers.setValue([]);
                    onFileSelected?.([]);
                    return;
                }
                handleMultipleFiles(selectedFile);
                return;
            }
            if (!selectedFile) {
                setFile(null);
                setPreview(null);
                helpers.setValue(null);
                onFileSelected?.(null);
                return;
            }

            const error = validateFile(selectedFile);

            if (error) {
                setLocalError(error);
                setFile(null);
                setPreview(null);
                helpers.setError(error);
                return;
            }

            // Reset the manually removed flag when adding a new file
            hasManuallyRemoved.current = false;

            setLocalError(null);
            helpers.setError(null);
            helpers.setValue(selectedFile);
            setFile(selectedFile);

            // Create preview for image files
            if (selectedFile.type?.startsWith("image/")) {
                const previewUrl = URL.createObjectURL(selectedFile);
                setPreview(previewUrl);
            }

            onFileSelected?.(selectedFile);
        },
        [
            validateFile,
            helpers,
            onFileSelected,
            multiple,
            fileType,
            handleMultipleFiles,
        ],
    );

    const handleFileChange = useCallback(
        (event) => {
            if (
                multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
            ) {
                const selectedFiles = Array.from(event.target.files || []);
                handleFile(selectedFiles);
            } else {
                const selectedFile = event.target.files?.[0];
                if (selectedFile) {
                    handleFile(selectedFile);
                }
            }
        },
        [handleFile, multiple, fileType],
    );

    const handleDrop = useCallback(
        (event) => {
            event.preventDefault();
            if (
                multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
            ) {
                const droppedFiles = Array.from(event.dataTransfer.files || []);
                handleFile(droppedFiles);
            } else {
                const droppedFile = event.dataTransfer.files?.[0];
                if (droppedFile) {
                    handleFile(droppedFile);
                }
            }
        },
        [handleFile, multiple, fileType],
    );

    // Remove a single file in multiple mode
    const handleRemoveImage = useCallback(
        (idx) => {
            if (
                multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
            ) {
                // Revoke the blob URL for the removed image
                if (preview[idx]?.startsWith("blob:")) {
                    URL.revokeObjectURL(preview[idx]);
                }
                const newFiles = file.filter((_, i) => i !== idx);
                const newPreviews = preview.filter((_, i) => i !== idx);
                setFile(newFiles);
                setPreview(newPreviews);
                helpers.setValue(newFiles);
                onFileSelected?.(newFiles);

                // Clear any validation errors when files are removed
                setLocalError(null);
                helpers.setError(null);

                if (inputRef.current) {
                    inputRef.current.value = "";
                }
                return;
            }
        },
        [file, preview, helpers, onFileSelected, multiple, fileType],
    );

    const handleRemoveFile = useCallback(() => {
        // Set flag to prevent re-initialization
        hasManuallyRemoved.current = true;

        // Clear all state
        setFile(
            multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
                ? []
                : null,
        );
        setPreview(
            multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
                ? []
                : null,
        );
        setLocalError(null);

        // Update form field
        helpers.setValue(
            multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
                ? []
                : null,
        );
        helpers.setError(null);
        helpers.setTouched(false);

        // Notify parent component if needed
        onFileSelected?.(
            multiple &&
                (fileType === "image" ||
                    fileType === "document" ||
                    fileType === "mixed")
                ? []
                : null,
        );

        // Reset input element
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    }, [helpers, onFileSelected, multiple, fileType]);

    useImperativeHandle(
        ref,
        () => ({
            clearFile: handleRemoveFile,
        }),
        [handleRemoveFile],
    );

    const renderThumbnail = useCallback(
        (fileOrUrl) => {
            if (!fileOrUrl) return null;

            // Handle URL string or file object with URL
            if (typeof fileOrUrl === "string" || (fileOrUrl && fileOrUrl.url)) {
                const url =
                    typeof fileOrUrl === "string" ? fileOrUrl : fileOrUrl.url;

                let fileExtension = "";

                if (fileOrUrl.file_name) {
                    fileExtension = fileOrUrl.file_name
                        .split(".")
                        .pop()
                        .toLowerCase();
                } else if (fileOrUrl.name) {
                    fileExtension = fileOrUrl.name
                        .split(".")
                        .pop()
                        .toLowerCase();
                } else {
                    const urlPath = url.split("?")[0]; // Remove query parameters
                    fileExtension = urlPath.split(".").pop().toLowerCase();
                }

                const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
                const isImage = imageExtensions.includes(fileExtension);

                if ((fileType === "image" || fileType === "mixed") && isImage) {
                    return (
                        <img
                            src={url}
                            alt="Preview"
                            height="80px"
                            width="80px"
                            className="rounded"
                            style={{ objectFit: "cover" }}
                        />
                    );
                } else {
                    const iconMap = {
                        pdf: "bi-file-earmark-pdf-fill text-danger",
                        doc: "bi-file-earmark-word-fill text-primary",
                        docx: "bi-file-earmark-word-fill text-primary",
                        xls: "bi-file-earmark-excel-fill text-success",
                        xlsx: "bi-file-earmark-excel-fill text-success",
                    };

                    const iconClass =
                        iconMap[fileExtension] ||
                        "bi-file-earmark text-secondary";
                    return (
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: "80px", height: "80px" }}
                        >
                            <i
                                className={`bi ${iconClass}`}
                                style={{ fontSize: "3rem" }}
                            />
                        </div>
                    );
                }
            }

            // Handle File object
            if (fileOrUrl instanceof File) {
                if (fileOrUrl.type.startsWith("image/")) {
                    // For image files, use the preview state if available, otherwise create a blob URL
                    const imageUrl =
                        preview && preview.startsWith("blob:")
                            ? preview
                            : URL.createObjectURL(fileOrUrl);

                    return (
                        <img
                            src={imageUrl}
                            alt="Preview"
                            height="80px"
                            width="80px"
                            className="rounded"
                            style={{ objectFit: "cover" }}
                        />
                    );
                }

                const iconMap = {
                    pdf: "bi-file-earmark-pdf-fill text-danger",
                    doc: "bi-file-earmark-word-fill text-primary",
                    docx: "bi-file-earmark-word-fill text-primary",
                    xls: "bi-file-earmark-excel-fill text-success",
                    xlsx: "bi-file-earmark-excel-fill text-success",
                };

                const fileExtension = fileOrUrl.name
                    .split(".")
                    .pop()
                    .toLowerCase();
                const iconClass =
                    iconMap[fileExtension] || "bi-file-earmark text-secondary";
                return (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: "80px", height: "80px" }}
                    >
                        <i
                            className={`bi ${iconClass}`}
                            style={{ fontSize: "3rem" }}
                        />
                    </div>
                );
            }
            return null;
        },
        [preview, fileType],
    );

    const getFileNameFromUrl = (url) => {
        if (!url) return "";

        // First check if we have a name from initialFile
        if (initialFile?.name && initialFile?.isExisting) {
            return initialFile.name;
        }

        try {
            // Extract the filename from the path (before the query parameters)
            const pathMatch = url.match(/\/([^/?]+)\?/);
            if (pathMatch && pathMatch[1]) {
                return decodeURIComponent(pathMatch[1]);
            }

            // If no match from path, try to extract from the end of URL
            const lastSegment = url.split("/").pop().split("?")[0];
            if (lastSegment) {
                return decodeURIComponent(lastSegment);
            }

            return fileType === "document"
                ? "Current Document"
                : "Current Image";
        } catch (e) {
            return fileType === "document"
                ? "Current Document"
                : "Current Image";
        }
    };

    // Effect to handle initialFile
    useEffect(() => {
        if (
            !initializedRef.current &&
            initialFile &&
            (!file || (Array.isArray(file) && file.length === 0)) &&
            (!preview || (Array.isArray(preview) && preview.length === 0)) &&
            !hasManuallyRemoved.current
        ) {
            if (multiple && Array.isArray(initialFile)) {
                setFile(initialFile);
                setPreview(initialFile.map((f) => f.url));
                helpers.setValue(initialFile);
            } else if (initialFile?.url) {
                setPreview(initialFile.url);
                setFile(initialFile);
                helpers.setValue(initialFile);
            }

            if (!field.value) {
                helpers.setValue(initialFile);
            }

            initializedRef.current = true;
        }

        // Set error if field is required and empty
        if (required && !file && !preview && !initialFile && field.touched) {
            helpers.setError("This field is required");
        }
    }, [
        initialFile,
        file,
        preview,
        helpers,
        field.value,
        required,
        field.touched,
        multiple,
        fileType,
    ]);

    useEffect(() => {
        if (
            multiple &&
            (fileType === "image" ||
                fileType === "document" ||
                fileType === "mixed")
        ) {
            if (Array.isArray(selectedFile)) {
                setFile(selectedFile);
                const previews = selectedFile.map((f) =>
                    f.url
                        ? f.url
                        : f instanceof File && f.type?.startsWith("image/")
                          ? URL.createObjectURL(f)
                          : null,
                );
                setPreview(previews);
                hasManuallyRemoved.current = false;
            }
        } else {
            if (selectedFile) {
                setFile(selectedFile);

                if (
                    selectedFile instanceof File &&
                    selectedFile.type?.startsWith("image/")
                ) {
                    const previewUrl = URL.createObjectURL(selectedFile);
                    setPreview(previewUrl);
                    // Reset the manually removed flag since we have a new file
                    hasManuallyRemoved.current = false;
                } else if (typeof selectedFile === "string") {
                    setPreview(selectedFile);
                    // Reset the manually removed flag since we have a new file
                    hasManuallyRemoved.current = false;
                }
            }
        }
    }, [selectedFile, multiple, fileType]);

    // Cleanup useEffect
    useEffect(() => {
        if (
            multiple &&
            (fileType === "image" ||
                fileType === "document" ||
                fileType === "mixed")
        ) {
            return () => {
                preview.forEach((p) => {
                    if (p?.startsWith("blob:")) {
                        URL.revokeObjectURL(p);
                    }
                });
            };
        } else {
            return () => {
                if (preview?.startsWith("blob:")) {
                    URL.revokeObjectURL(preview);
                }
            };
        }
    }, [preview, multiple, fileType]);

    return (
        <div className="w-100">
            {label && (
                <label htmlFor={name} className="form-label">
                    {label}
                    {required && <span className="text-danger ms-1">*</span>}
                </label>
            )}
            <section
                className="drag-drop d-flex flex-column"
                style={{ width, height }}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
            >
                <div
                    className={`document-uploader ${
                        (multiple &&
                            (fileType === "image" || fileType === "document") &&
                            file.length > 0) ||
                        (!multiple && (file || selectedFile))
                            ? "upload-box active"
                            : "upload-box"
                    }`}
                >
                    <div className="upload-info d-flex flex-column align-items-center">
                        <i className="bi bi-cloud-arrow-up upload-icon"></i>
                        <Typography variant="body1" className="fw-bold mb-1">
                            {fileType === "document" ||
                            fileType === "mixed" ||
                            customFileTypes
                                ? "Drag and drop your documents here, or "
                                : "Drag and drop your file here, or "}
                            <label
                                htmlFor={name}
                                className="browse-btn btn-link p-0"
                            >
                                browse
                            </label>
                        </Typography>
                        <Typography variant="body1" className="text-muted">
                            Limit {maxFileSize / (1024 * 1024)}MB per file.
                            Supported files:{" "}
                            {customFileTypes
                                ? customFileTypes.extensions
                                      .map((ext) => ext.toUpperCase())
                                      .join(", ")
                                : fileType === "document"
                                  ? "PDF, DOC, DOCX, XLS, XLSX"
                                  : fileType === "mixed"
                                    ? "PNG, JPG, JPEG, PDF"
                                    : getAllowedTypes()
                                          .extensions.map((ext) =>
                                              ext.toUpperCase(),
                                          )
                                          .join(", ")}
                        </Typography>
                    </div>
                    <input
                        id={name}
                        name={name}
                        type="file"
                        accept={getAllowedTypes().mimeTypes.join(",")}
                        hidden
                        ref={inputRef}
                        onChange={handleFileChange}
                        multiple={
                            multiple &&
                            (fileType === "image" ||
                                fileType === "document" ||
                                fileType === "mixed")
                        }
                        {...rest}
                    />

                    {/* Multiple file preview */}
                    {multiple &&
                        (fileType === "image" ||
                            fileType === "document" ||
                            fileType === "mixed") &&
                        preview.length > 0 &&
                        !hasManuallyRemoved.current && (
                            <div className="file-list d-flex flex-column gap-2 w-100 h-30">
                                <div className="file-list__container w-100 h-100 overflow-auto">
                                    {preview.map((p, idx) => (
                                        <div
                                            key={idx}
                                            className="file-item d-flex justify-content-between align-items-center p-2 border rounded mb-2"
                                        >
                                            <div className="file-info d-flex align-items-center gap-1 flex-grow-1">
                                                {(fileType === "image" ||
                                                    fileType === "mixed") &&
                                                    p && (
                                                        <img
                                                            src={p}
                                                            alt="Preview"
                                                            height="80px"
                                                            width="80px"
                                                            className="rounded"
                                                            style={{
                                                                objectFit:
                                                                    "cover",
                                                            }}
                                                        />
                                                    )}
                                                {(fileType === "document" ||
                                                    (fileType === "mixed" &&
                                                        !p)) &&
                                                    file[idx] && (
                                                        <div
                                                            className="document-icon d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "80px",
                                                                height: "80px",
                                                            }}
                                                        >
                                                            {renderThumbnail(
                                                                file[idx],
                                                            )}
                                                        </div>
                                                    )}
                                                <Typography
                                                    variant="body1"
                                                    className="ms-2 m-0"
                                                >
                                                    {file[idx]?.name ||
                                                        `Document ${idx + 1}`}
                                                </Typography>
                                            </div>
                                            <div className="file-actions cursor-pointer">
                                                <i
                                                    className="bi bi-x fs-3 text-secondary"
                                                    onClick={() =>
                                                        handleRemoveImage(idx)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    {/* Single file preview (existing logic) */}
                    {!(
                        multiple &&
                        (fileType === "image" ||
                            fileType === "document" ||
                            fileType === "mixed")
                    ) &&
                        (file || selectedFile || initialFile?.url) &&
                        !hasManuallyRemoved.current && (
                            <div className="file-list d-flex flex-column gap-2 w-100 h-30">
                                <div className="file-list__container w-100 h-100 overflow-auto">
                                    <div className="file-item d-flex justify-content-between align-items-center p-2 border rounded">
                                        <div className="file-info d-flex align-items-center gap-1 flex-grow-1">
                                            {renderThumbnail(
                                                selectedFile ||
                                                    file ||
                                                    initialFile?.url,
                                            )}
                                            <Typography
                                                variant="body1"
                                                className="ms-2 m-0"
                                            >
                                                {file?.name ||
                                                    (selectedFile instanceof
                                                    File
                                                        ? selectedFile.name
                                                        : initialFile?.url
                                                          ? getFileNameFromUrl(
                                                                initialFile.url,
                                                            )
                                                          : fileType ===
                                                              "document"
                                                            ? "Current Document"
                                                            : "Current Image")}
                                            </Typography>
                                        </div>
                                        <div className="file-actions cursor-pointer">
                                            <i
                                                className="bi bi-x fs-3 text-secondary"
                                                onClick={handleRemoveFile}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            </section>
            {(meta.touched || localError) && (meta.error || localError) && (
                <div className="text-danger mt-2">
                    {meta.error || localError}
                </div>
            )}
        </div>
    );
});

export default UnifiedFileUpload;
